'use client';

import { c as _c } from "react/compiler-runtime";
import { motion, useIsPresent, useReducedMotion } from 'motion/react';
import { ResponsiveImage } from '@/app/components/media/app-image';
import { SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_LABELS } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/constants';
import { getSpartanProductExteriorThreesixtyPresetMotion } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/preset-thumbnails/motion';
import { getSpartanProductExteriorThreesixtyPresetThumbnailSources } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/preset-thumbnails/sources';
import styles from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/preset-thumbnails/styles.module.css';
import type { SpartanProductExteriorThreesixtyPresetThumbnailsProps } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/preset-thumbnails/types';
import { SPARTAN_PRODUCT_EXTERIOR_DATA_GA, SPARTAN_PRODUCT_EXTERIOR_TRACKING } from '@/app/lcv/components/sections/spartan/products/exterior/tracking';
import { SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/icon-button';
import { SPARTAN_PRODUCTS_EVENT_NAME } from '@/app/lcv/components/sections/spartan/products/shared/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
export const SpartanProductExteriorThreesixtyPresetThumbnails = t0 => {
  const $ = _c(34);
  const {
    frames,
    indexes,
    activeIndex,
    disabled,
    onInteractionStart,
    onPointerEnter,
    onSelect
  } = t0;
  const shouldReduceMotion = useReducedMotion() ?? false;
  const isPresent = useIsPresent();
  const isInteractionDisabled = disabled || !isPresent;
  let t1;
  if ($[0] !== shouldReduceMotion) {
    t1 = getSpartanProductExteriorThreesixtyPresetMotion(shouldReduceMotion);
    $[0] = shouldReduceMotion;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const presetMotion = t1;
  const t2 = presetMotion.group;
  const t3 = !isPresent || undefined;
  const t4 = !isPresent ? true : undefined;
  const t5 = isPresent ? "pointer-events-auto" : "pointer-events-none";
  let t6;
  if ($[2] !== t5) {
    t6 = cn("relative w-full min-w-0", styles.presetsNav, t5);
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = createDataAttributes({
      test: SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyPresets
    });
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== presetMotion.background) {
    t8 = <motion.div aria-hidden="true" variants={presetMotion.background} className="pointer-events-none absolute inset-0 rounded-lg bg-white/50" />;
    $[5] = presetMotion.background;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = presetMotion.list;
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("relative mx-auto flex w-full min-w-0 items-center justify-center self-center p-1", styles.presetsList);
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== activeIndex || $[9] !== frames || $[10] !== indexes || $[11] !== isInteractionDisabled || $[12] !== onInteractionStart || $[13] !== onSelect || $[14] !== presetMotion.item) {
    let t12;
    if ($[16] !== activeIndex || $[17] !== frames || $[18] !== isInteractionDisabled || $[19] !== onInteractionStart || $[20] !== onSelect || $[21] !== presetMotion.item) {
      t12 = (frameIndex, index) => {
        const isActive = activeIndex === frameIndex;
        const label = SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_LABELS.presets[index] ?? `${SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_LABELS.presetViews} ${index + 1}`;
        const {
          mobileSrc,
          desktopSrc
        } = getSpartanProductExteriorThreesixtyPresetThumbnailSources(frames[frameIndex]);
        return <motion.li key={frameIndex} variants={presetMotion.item} className={cn("flex min-w-0 flex-1 basis-0 items-center justify-center", styles.presetItem)}><button type="button" aria-label={label} aria-pressed={isActive} disabled={isInteractionDisabled} onPointerDown={event_0 => {
            event_0.stopPropagation();
            onInteractionStart();
          }} onTouchStart={event_1 => {
            event_1.stopPropagation();
            onInteractionStart();
          }} onTouchEnd={event_2 => {
            event_2.preventDefault();
            event_2.stopPropagation();
            onInteractionStart();
            if (!isInteractionDisabled) {
              onSelect(frameIndex);
            }
          }} onKeyDown={_temp2} onClick={() => onSelect(frameIndex)} className={cn("relative block w-full cursor-pointer overflow-hidden rounded-md border-2 p-0.5 transition-[border-color,opacity] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-800 disabled:cursor-default disabled:opacity-65 motion-reduce:transition-none", SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME, isActive ? "border-gray-800" : "border-transparent hover:border-gray-800 focus-visible:border-gray-800")} {...createDataAttributes({
            test: SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyPreset(index),
            active: isActive ? "true" : null
          })} {...createDataGA({
            click: true,
            eventName: SPARTAN_PRODUCTS_EVENT_NAME,
            group: SPARTAN_PRODUCT_EXTERIOR_DATA_GA.section,
            text: label,
            value: SPARTAN_PRODUCT_EXTERIOR_DATA_GA.preset
          })}><ResponsiveImage mobileSrc={mobileSrc} desktopSrc={desktopSrc} alt="" width={320} height={180} loading="eager" decoding="async" fade={false} objectFit="cover" imgStyle={{
              objectPosition: "50% 40%"
            }} className="aspect-16-9 pointer-events-none w-full overflow-hidden rounded-sm" testId={SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyPresetImage(index)} /></button></motion.li>;
      };
      $[16] = activeIndex;
      $[17] = frames;
      $[18] = isInteractionDisabled;
      $[19] = onInteractionStart;
      $[20] = onSelect;
      $[21] = presetMotion.item;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    t11 = indexes.map(t12);
    $[8] = activeIndex;
    $[9] = frames;
    $[10] = indexes;
    $[11] = isInteractionDisabled;
    $[12] = onInteractionStart;
    $[13] = onSelect;
    $[14] = presetMotion.item;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[23] !== presetMotion.list || $[24] !== t11) {
    t12 = <motion.ul variants={t9} className={t10}>{t11}</motion.ul>;
    $[23] = presetMotion.list;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== onPointerEnter || $[27] !== presetMotion.group || $[28] !== t12 || $[29] !== t3 || $[30] !== t4 || $[31] !== t6 || $[32] !== t8) {
    t13 = <motion.nav initial="initial" animate="animate" exit="exit" variants={t2} aria-label={SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_LABELS.presetViews} aria-hidden={t3} inert={t4} onPointerEnter={onPointerEnter} onPointerMove={_temp} className={t6} {...t7}>{t8}{t12}</motion.nav>;
    $[26] = onPointerEnter;
    $[27] = presetMotion.group;
    $[28] = t12;
    $[29] = t3;
    $[30] = t4;
    $[31] = t6;
    $[32] = t8;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
};
function _temp(event) {
  return event.stopPropagation();
}
function _temp2(event_3) {
  return event_3.stopPropagation();
}